import { type ReactNode, useId } from "react";

import { ListItem, ListItemContent, ListItemTrailing } from "@/components/list";
import Text from "@/components/text";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Label-and-control rows shared by the new-run forms. */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2 px-(--list-inset) py-2.5">
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
      {children(id)}
      {hint && <Text.Hint size="caption">{hint}</Text.Hint>}
    </div>
  );
}

export function NumberRow({
  label,
  hint,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  const id = useId();
  return (
    <ListItem>
      <ListItemContent>
        <Label htmlFor={id} className="font-normal">
          {label}
        </Label>
        {hint && <Text.Hint size="caption">{hint}</Text.Hint>}
      </ListItemContent>
      <ListItemTrailing>
        <Input
          id={id}
          type="number"
          className="h-(--control-height-sm) w-24 text-end tabular-nums"
          value={Number.isFinite(value) ? value : ""}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(e.target.valueAsNumber)}
        />
      </ListItemTrailing>
    </ListItem>
  );
}

export function CheckRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <ListItem asChild>
      <label htmlFor={id} className={disabled ? "opacity-60" : "cursor-pointer"}>
        <ListItemContent>
          <Text>{label}</Text>
          <Text.Hint size="caption">{hint}</Text.Hint>
        </ListItemContent>
        <ListItemTrailing>
          <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(v === true)} />
        </ListItemTrailing>
      </label>
    </ListItem>
  );
}
